<template>
    <div>
        <div
            v-if="!isMobile"
            class="flex w-full max-w-[50rem] flex-wrap justify-end gap-5 max-lg:gap-3 max-md:hidden">
            <BurgerMenuCategoryCard
                v-for="(card, index) in cardsData"
                :key="'desktop-' + index"
                :to="card.to"
                :animation="card.animation"
                :title="card.title"
                class="pointer-events-auto relative">
                <picture :class="card.pictureClass">
                    <source
                        v-if="card.imageWebp"
                        type="image/webp"
                        :srcset="card.imageWebp" />
                    <img
                        :width="card.width"
                        :height="card.height"
                        loading="lazy"
                        decoding="async"
                        :alt="card.title"
                        :src="card.imagePng"
                        :class="card.imageClass" />
                </picture>
                <template v-if="card.additionalPicture">
                    <picture>
                        <source
                            type="image/webp"
                            :srcset="card.additionalPicture.webp" />
                        <img
                            :width="card.width"
                            :height="card.height"
                            :alt="card.title"
                            decoding="async"
                            loading="lazy"
                            :src="card.additionalPicture.png"
                            :class="card.additionalPicture.class" />
                    </picture>
                </template>
            </BurgerMenuCategoryCard>
        </div>

        <div
            v-show="isMobile"
            class="flex w-full flex-wrap gap-4 lg:hidden">
            <div
                v-for="(row, rowIndex) in mobileCardsData"
                :key="'mobile-row-' + rowIndex"
                class="flex w-full gap-1 mobile:gap-2">
                <BurgerMenuCategoryCard
                    v-for="(card, cardIndex) in row"
                    :key="'mobile-' + rowIndex + '-' + cardIndex"
                    :to="card.to"
                    :size="card.size"
                    :is-show-noise="false"
                    :animation="card.animation"
                    :title="card.title"
                    class="pointer-events-auto">
                    <picture :class="card.pictureClass">
                        <source
                            v-if="card.imageWebp"
                            type="image/webp"
                            :srcset="card.imageWebp" />
                        <img
                            :alt="card.title"
                            :src="card.imagePng"
                            loading="lazy"
                            decoding="async"
                            :class="card.imageClass" />
                    </picture>
                </BurgerMenuCategoryCard>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { useMediaQuery } from '@vueuse/core'

import BurgerMenuCategoryCard from '~/components/modals/burger-menu/BurgerMenuCategoryCard.vue'

const config = useRuntimeConfig()
const isMobile = useMediaQuery('(max-width: 1024px)')

type BurgerMenuCardAnimation = 'rotate-right' | 'rotate-left' | 'scale' | 'none'
type BurgerMenuCardSize = 'small' | 'medium' | 'large'

interface BurgerMenuCardPicture {
    class: string
    webp: string
    png: string
}

interface BurgerMenuCardItem {
    to: string
    animation?: BurgerMenuCardAnimation
    title: string
    width: number
    height: number
    imageWebp?: string
    imagePng: string
    pictureClass?: string
    imageClass?: string
    additionalPicture?: BurgerMenuCardPicture
    size?: BurgerMenuCardSize
}

const cardsDataCromi: BurgerMenuCardItem[] = [
    {
        to: '/radioguide',
        animation: 'scale',
        title: 'Радиогиды',
        width: 549,
        height: 934,
        imageWebp:
            '/assets/images/products/radioguide/webp/reinvox-duo-bright.webp',
        imagePng:
            '/assets/images/products/radioguide/jpg-png/reinvox-duo-bright.png',
        imageClass: 'h-auto w-[4rem] lg:w-[7.5rem]',
    },
    {
        to: '/audioguide',
        animation: 'rotate-right',
        title: 'Аудиогиды',
        width: 212,
        height: 718,
        imageWebp: '/assets/images/products/audioguide/webp/a1-bright.webp',
        imagePng: '/assets/images/products/audioguide/jpg-png/a1-bright.png',
        imageClass: 'h-auto w-[4.75rem]',
    },
    {
        to: '/cabin',
        animation: 'scale',
        title: 'Кабины переводчиков',
        width: 690,
        height: 713,
        imageWebp: '/assets/images/products/cabin/webp/eurocab-1.webp',
        imagePng: '/assets/images/products/cabin/jpg-png/eurocab-1.png',
        imageClass: 'h-auto w-[8.55rem]',
    },
    {
        to: '/sinhroperevod',
        animation: 'rotate-left',
        title: 'Оборудование синхронного перевода',
        width: 554,
        height: 306,
        pictureClass:
            'absolute -top-2 left-2 h-auto w-[12rem] max-w-none lg:left-0 lg:top-0 lg:w-[15.625rem]',
        imageWebp:
            '/assets/images/products/sinhroperevod/webp/sinhroperevod.webp',
        imagePng:
            '/assets/images/products/sinhroperevod/jpg-png/sinhroperevod.png',
    },
    {
        to: '/conference-system',
        animation: 'rotate-left',
        title: 'Конференц-системы',
        width: 1300,
        height: 664,
        pictureClass:
            'absolute -top-2 h-auto w-[13.5rem] max-w-none lg:-top-1 lg:w-[18rem]',
        imageWebp:
            '/assets/images/products/conference-system/webp/reinvox-concept.webp',
        imagePng:
            '/assets/images/products/conference-system/jpg-png/reinvox-concept.png',
    },
    {
        to: '/dostupsreda',
        animation: 'scale',
        title: 'Доступная среда',
        width: 776,
        height: 701,
        imageWebp:
            '/assets/images/products/available-environment/webp/spbaudio-tiflo-fm.webp',
        imagePng:
            '/assets/images/products/available-environment/jpg-png/spbaudio-tiflo-fm.png',
        imageClass:
            'absolute top-0 left-1/2 -translate-x-1/2 h-auto w-[8rem] max-w-none',
    },
    {
        to: '/catalog',
        title: 'Все товары',
        width: 360,
        height: 280,
        additionalPicture: {
            class: 'absolute left-0 -top-6 h-[8.75rem] w-[11.25rem] object-cover transition duration-[1.25s] group-hover:scale-110 group-hover:opacity-0 group-hover:blur-md lg:-top-3',
            webp: '/assets/images/ui/other/webp/all-products-fade.webp',
            png: '/assets/images/ui/other/jpg-png/all-products-fade.png',
        },
        imageWebp: '/assets/images/ui/other/webp/all-products.webp',
        imagePng: '/assets/images/ui/other/jpg-png/all-products.png',
        imageClass:
            'absolute left-0 -top-6 h-[8.75rem] w-[11.25rem] scale-110 object-cover opacity-0 blur-md transition duration-[1.25s] group-hover:scale-100 group-hover:opacity-100 group-hover:blur-0 lg:-top-3',
    },
]

const mobileCardsDataCromi: BurgerMenuCardItem[][] = [
    [
        {
            to: '/radioguide',
            size: 'medium',
            animation: 'scale',
            title: 'Радиогиды',
            width: 549,
            height: 934,
            imageWebp:
                '/assets/images/ui/bm-mobile/webp/reinvox-duo-bright-min.webp',
            imagePng:
                '/assets/images/ui/bm-mobile/jpg-png/reinvox-duo-bright-min.png',
            pictureClass: 'h-auto w-[4rem]',
            imageClass: 'h-auto w-[4rem]',
        },
        {
            to: '/audioguide',
            size: 'medium',
            animation: 'rotate-right',
            title: 'Аудиогиды',
            width: 212,
            height: 718,
            imageWebp: '/assets/images/ui/bm-mobile/webp/a1-bright-min.webp',
            imagePng: '/assets/images/ui/bm-mobile/jpg-png/a1-bright-min.png',
            pictureClass: 'h-auto w-[2.75rem]',
            imageClass: 'h-auto w-[2.75rem]',
        },
    ],
    [
        {
            to: '/cabin',
            size: 'medium',
            animation: 'scale',
            title: 'Кабины переводчиков',
            width: 690,
            height: 713,
            imageWebp: '/assets/images/ui/bm-mobile/webp/eurocab-1-min.webp',
            imagePng: '/assets/images/ui/bm-mobile/jpg-png/eurocab-1-min.png',
            pictureClass: 'h-auto w-[5.4rem]',
        },
        {
            to: '/sinhroperevod',
            size: 'medium',
            animation: 'rotate-left',
            title: 'Оборудование синхронного перевода',
            width: 554,
            height: 306,
            pictureClass: 'absolute -top-2 left-2 h-auto w-[9rem] max-w-none',
            imageWebp:
                '/assets/images/ui/bm-mobile/webp/sinhroperevod-min.webp',
            imagePng:
                '/assets/images/ui/bm-mobile/jpg-png/sinhroperevod-min.png',
        },
    ],
    [
        {
            to: '/conference-system',
            size: 'small',
            animation: 'rotate-left',
            title: 'Конференц-системы',
            width: 1300,
            height: 664,
            pictureClass: 'absolute left-0 top-0 h-auto w-full max-w-none',
            imageWebp:
                '/assets/images/ui/bm-mobile/webp/reinvox-concept-min.webp',
            imagePng:
                '/assets/images/ui/bm-mobile/jpg-png/reinvox-concept-min.png',
        },
        {
            to: '/dostupsreda',
            size: 'small',
            animation: 'scale',
            title: 'Доступная среда',
            width: 776,
            height: 701,
            pictureClass:
                'absolute left-1/2 -translate-x-1/2 -top-1 h-auto w-16 max-w-none',
            imageWebp:
                '/assets/images/products/available-environment/webp/spbaudio-tiflo-fm.webp',
            imagePng:
                '/assets/images/products/available-environment/jpg-png/spbaudio-tiflo-fm.png',
        },
        {
            to: '/catalog',
            size: 'small',
            title: 'Все товары',
            width: 360,
            height: 280,
            pictureClass: 'absolute -top-3 h-[5rem] w-[5.8rem] object-cover',
            imageWebp: '/assets/images/ui/bm-mobile/webp/all-products-min.webp',
            imagePng:
                '/assets/images/ui/bm-mobile/jpg-png/all-products-min.png',
        },
    ],
]

const cardsDataRent: BurgerMenuCardItem[] = [
    {
        to: '/arenda-oborudovanie-dlya-sinhronnogo-perevoda',
        animation: 'scale',
        title: 'Оборудование синхронного перевода',
        width: 137,
        height: 128,
        pictureClass: '',
        imageWebp: '/assets/images/products/cabin/webp/eurocab-rent.webp',
        imagePng: '/assets/images/products/cabin/jpg-png/eurocab-rent.png',
    },
    {
        to: '/arenda-konferents-system',
        animation: 'rotate-left',
        title: 'Конференц-системы',
        width: 536,
        height: 564,
        pictureClass: 'w-[5.5rem] lg:w-[8rem]',
        imageWebp:
            '/assets/images/products/conference-system/webp/bosch-dcn-disl.webp',
        imagePng:
            '/assets/images/products/conference-system/jpg-png/bosch-dcn-disl.png',
    },
    {
        to: '/arenda-zvuka',
        animation: 'scale',
        title: 'Звуковое оборудование',
        width: 479,
        height: 328,
        imageWebp: '/assets/images/products/sound/webp/microphone.webp',
        imagePng: '/assets/images/products/sound/jpg-png/microphone.png',
        imageClass: 'absolute right-0',
    },
    {
        to: '/arenda-video',
        animation: 'scale',
        title: 'Видеоконференции и онлайн-мероприятия',
        width: 878,
        height: 778,
        imageWebp: '/assets/images/products/video/webp/camera-bright.webp',
        imagePng: '/assets/images/products/video/jpg-png/camera-bright.png',
        imageClass: 'h-auto w-[4rem] lg:w-[7.5rem]',
    },
    {
        to: '/arenda-radioguides',
        animation: 'scale',
        title: 'Радиогиды',
        width: 549,
        height: 934,
        imageWebp:
            '/assets/images/products/radioguide/webp/reinvox-duo-bright.webp',
        imagePng:
            '/assets/images/products/radioguide/jpg-png/reinvox-duo-bright.png',
        imageClass: 'h-auto w-[4rem] lg:w-[7.5rem]',
    },
    {
        to: '/arenda-audioguides',
        animation: 'rotate-right',
        title: 'Аудиогиды',
        width: 212,
        height: 718,
        imageWebp: '/assets/images/products/audioguide/webp/a1-bright.webp',
        imagePng: '/assets/images/products/audioguide/jpg-png/a1-bright.png',
        imageClass: 'h-auto w-[4.75rem]',
    },
    {
        to: '/catalog',
        title: 'Все товары',
        width: 360,
        height: 280,
        additionalPicture: {
            class: 'absolute left-0 -top-6 h-[8.75rem] w-[11.25rem] object-cover transition duration-[1.25s] group-hover:scale-110 group-hover:opacity-0 group-hover:blur-md lg:-top-3',
            webp: '/assets/images/ui/other/webp/all-products-fade.webp',
            png: '/assets/images/ui/other/jpg-png/all-products-fade.png',
        },
        imageWebp: '/assets/images/ui/other/webp/all-products.webp',
        imagePng: '/assets/images/ui/other/jpg-png/all-products.png',
        imageClass:
            'absolute left-0 -top-6 h-[8.75rem] w-[11.25rem] scale-110 object-cover opacity-0 blur-md transition duration-[1.25s] group-hover:scale-100 group-hover:opacity-100 group-hover:blur-0 lg:-top-3',
    },
]

const mobileCardsDataRent: BurgerMenuCardItem[][] = [
    [
        {
            to: '/arenda-oborudovanie-dlya-sinhronnogo-perevoda',
            animation: 'scale',
            size: 'medium',
            title: 'Оборудование синхронного перевода',
            width: 137,
            height: 128,
            pictureClass: 'h-auto w-[6rem]',
            imageClass: 'h-auto w-[6rem]',
            imageWebp: '/assets/images/products/cabin/webp/eurocab-rent.webp',
            imagePng: '/assets/images/products/cabin/jpg-png/eurocab-rent.png',
        },
        {
            to: '/arenda-konferents-system',
            animation: 'rotate-left',
            size: 'medium',
            title: 'Конференц-системы',
            width: 536,
            height: 564,
            pictureClass: 'h-auto w-[4rem]',
            imageClass: 'h-auto w-[4rem]',
            imageWebp:
                '/assets/images/products/conference-system/webp/bosch-dcn-disl.webp',
            imagePng:
                '/assets/images/products/conference-system/jpg-png/bosch-dcn-disl.png',
        },
    ],

    [
        {
            to: '/arenda-zvuka',
            animation: 'scale',
            size: 'medium',
            pictureClass: 'h-auto w-[6rem]',
            imageClass: 'h-auto w-[6rem]',
            title: 'Звуковое оборудование',
            width: 479,
            height: 328,
            imageWebp: '/assets/images/products/sound/webp/microphone.webp',
            imagePng: '/assets/images/products/sound/jpg-png/microphone.png',
        },
        {
            to: '/arenda-video',
            animation: 'scale',
            size: 'medium',
            pictureClass: 'h-auto -mt-4 w-[6rem]',
            imageClass: 'h-auto w-[6rem]',
            title: 'Видеоконференции и онлайн-мероприятия',
            width: 878,
            height: 778,
            imageWebp: '/assets/images/products/video/webp/camera-bright.webp',
            imagePng: '/assets/images/products/video/jpg-png/camera-bright.png',
        },
    ],

    [
        {
            to: '/arenda-radioguides',
            size: 'small',
            animation: 'scale',
            title: 'Радиогиды',
            width: 549,
            height: 934,
            imageWebp:
                '/assets/images/products/radioguide/webp/reinvox-duo-bright.webp',
            imagePng:
                '/assets/images/products/radioguide/jpg-png/reinvox-duo-bright.png',
            pictureClass: 'h-auto w-[2.75rem]',
            imageClass: 'h-auto w-[2.75rem]',
        },
        {
            to: '/arenda-audioguides',
            size: 'small',
            animation: 'rotate-right',
            title: 'Аудиогиды',
            width: 212,
            height: 718,
            imageWebp: '/assets/images/products/audioguide/webp/a1-bright.webp',
            imagePng:
                '/assets/images/products/audioguide/jpg-png/a1-bright.png',
            pictureClass: 'h-auto w-[2.75rem]',
            imageClass: 'h-auto w-[2.75rem]',
        },
        {
            to: '/catalog',
            size: 'small',
            title: 'Все товары',
            width: 360,
            height: 280,
            additionalPicture: {
                class: 'absolute -top-6 h-[8.75rem] w-[11.25rem] object-cover transition duration-[1.25s] group-hover:scale-110 group-hover:opacity-0 group-hover:blur-md lg:-top-3',
                webp: '/assets/images/ui/other/webp/all-products-fade.webp',
                png: '/assets/images/ui/other/jpg-png/all-products-fade.png',
            },
            imageWebp: '/assets/images/ui/other/webp/all-products.webp',
            imagePng: '/assets/images/ui/other/jpg-png/all-products.png',
            pictureClass: 'absolute -top-3 h-[5rem] w-[5.8rem] object-cover',
        },
    ],
]

const cardsData = computed(() => {
    if (config.public.website == 'cromi.rent') return cardsDataRent
    return cardsDataCromi
})

const mobileCardsData = computed(() => {
    if (config.public.website == 'cromi.rent') return mobileCardsDataRent

    return mobileCardsDataCromi
})
</script>
